import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Tenant } from '../../../model/tenant';
import { ActionButton } from '../../../model/ui/action-button';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { TenantsService } from '../../../services/tenants.service';
import { SortOrder } from '../../../model/sort-order';
import { Organization } from '../../../model/organization';
import { Kpi } from '../../../model/kpi';
import { KpiFilters } from '../../../model/filters/kpi-filters';
import { KpisService } from '../../../services/kpis.service';
import { translate } from '@ngneat/transloco';

@Component({
  selector: 'app-kpis-selector',
  templateUrl: './kpis-selector.component.html',
  styleUrls: ['./kpis-selector.component.scss']
})
export class KpisSelectorComponent {

  readonly DEFAULT_SORT_FIELD = 'name';
  readonly DEFAULT_SORT_ORDER = 1;

  @Input() tenant: Tenant;
  @Input() organization: Organization;
  @Input() actionButtons: ActionButton[];
  @Output() kpisChange: EventEmitter<Kpi[]>;
  @Output() visibleChange: EventEmitter<boolean>;

  dialogVisible: boolean;
  kpiList: Kpi[];
  initialSelection: Kpi[];
  totalKpis: number;
  loadingKpis: boolean;
  first: number;
  rows: number;
  categories: SelectItem<string>[];
  organizations: Organization[];
  currentFilters: KpiFilters;
  selectedKpis: Kpi[];

  hasBeenOpened: boolean;

  constructor(
    private tenantsService: TenantsService,
    private confirmationService: ConfirmationService,
    private kpisService: KpisService
  ) {
    this.visibleChange = new EventEmitter<boolean>();
    this.kpisChange = new EventEmitter<Kpi[]>();
    this.first = 0;
    this.rows = 10;
    this.currentFilters = new KpiFilters();
    this.currentFilters.sortField = this.DEFAULT_SORT_FIELD;
    this.currentFilters.sortOrder = this.DEFAULT_SORT_ORDER;
    this.loadingKpis = false;
    this.hasBeenOpened = false;
    this.kpiList = new Array(this.rows);
  }

  @Input()
  set visible(visible: boolean) {
    this.dialogVisible = visible;
    if (visible) {
      this.hasBeenOpened = true;
      this.loadOrganizations(this.tenant.uuid);
      this.loadKpis(0, this.rows);
      this.currentFilters.tenantUuids = [this.tenant.uuid];
      if (this.organization) {
        this.currentFilters.organizationUuids = [this.organization.uuid];
      }
    }
  }

  @Input()
  set kpis(kpis: Kpi[]) {
    this.selectedKpis = [...kpis];
    this.initialSelection = kpis
  }

  applyFilters(): void {
    this.first = 0;
    this.loadKpis(this.first / this.rows, this.rows);
  }

  onKpiPageChange(event: any): void {
    if (this.hasBeenOpened) {
      this.currentFilters.sortField = event.sortField;
      this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
      this.loadKpis(event.first / event.rows, event.rows);
    }
  }

  private loadKpis(offset: number, limit: number): void {
    this.loadingKpis = true;
    this.currentFilters.tenantUuids = [this.tenant.uuid];
    this.currentFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.currentFilters.organizationUuids;
    this.kpisService.getKpis(offset, limit, this.currentFilters).subscribe(response => {
      this.kpiList = response.kpis;
      this.totalKpis = response.totalElements;
      this.loadingKpis = false;
    });
  }

  private loadOrganizations(tenantUuid: string): void {
    this.tenantsService.getTenantOrganizationSummaries(tenantUuid).subscribe(value => {
      this.organizations = value.organizations;
    });
  }

  public displaySelectAllDialog(): void {
    this.confirmationService.confirm({
      message: translate('info.confirmSelectAll', { totalElements: this.totalKpis }),
      header: translate('common.selectAll'),
      acceptLabel: translate('common.accept'),
      rejectLabel: translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.selectAll()
    });
  }

  public selectAll(): void {
    this.currentFilters.tenantUuids = [this.tenant.uuid];
    this.currentFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.currentFilters.organizationUuids;
    this.kpisService.getKpis(null, null, this.currentFilters).subscribe(response => {
      this.selectedKpis = [...this.selectedKpis];
      this.selectedKpis.push(...response.kpis);
    });
  }

  public closeWithoutSaving(): void {
    this.selectedKpis = this.initialSelection;
    this.visible = false;
    this.visibleChange.emit(false);
  }

}
